import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/Table';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Receipt, Search, Plus, Trash2, AlertCircle } from 'lucide-react';
import { MOCK_USERS } from '../data/mockData';
import { useAdjustments, addAdjustment, removeAdjustment, AdjustmentType } from '../data/feesStore';
import { formatCurrency, cn } from '../lib/utils';
export function FeesRecoupments() {
  const adjustments = useAdjustments();
  const [search, setSearch] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  // Form state
  const [type, setType] = useState<AdjustmentType>('fee');
  const [userId, setUserId] = useState<string>('');
  const [loanId, setLoanId] = useState<string>('');
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [note, setNote] = useState('');
  const selectedUser = useMemo(() => MOCK_USERS.find(u => u.id === userId), [userId]);
  const userLoans = useMemo(() => {
    if (!selectedUser) return [];
    // Combine all loans from monthly history to allow selecting past loans
    const allLoans = selectedUser.monthlyLoans.flatMap(m => m.loans);
    // Deduplicate by id
    const uniqueLoans = Array.from(new Map(allLoans.map(l => [l.id, l])).values());
    return uniqueLoans;
  }, [selectedUser]);
  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId || !label || !amount || !date) return;
    const selectedLoan = userLoans.find(l => l.id === loanId);
    addAdjustment({
      type,
      userId,
      loanId: loanId || null,
      loanLabel: selectedLoan ? selectedLoan.borrowerName : null,
      label,
      amount: parseFloat(amount),
      date,
      note
    });
    // Reset form
    setIsAdding(false);
    setUserId('');
    setLoanId('');
    setLabel('');
    setAmount('');
    setNote('');
  };
  const enrichedAdjustments = useMemo(() => {
    return adjustments.map(adj => {
      const user = MOCK_USERS.find(u => u.id === adj.userId);
      return {
        ...adj,
        user
      };
    });
  }, [adjustments]);
  const filteredAdjustments = enrichedAdjustments.filter(adj => {
    const searchLower = search.toLowerCase();
    return adj.label.toLowerCase().includes(searchLower) || adj.user?.name.toLowerCase().includes(searchLower) || adj.loanLabel?.toLowerCase().includes(searchLower);
  });
  const totalFees = adjustments.filter(a => a.type === 'fee').reduce((sum, a) => sum + a.amount, 0);
  const totalRecoupments = adjustments.filter(a => a.type === 'recoupment').reduce((sum, a) => sum + a.amount, 0);
  return <div className="space-y-6" data-id="element-188">
      <div className="flex items-center justify-between pb-2" data-id="element-189">
        <div data-id="element-190">
          <h1 className="text-2xl font-semibold text-slate-900" data-id="element-191">
            Fees & Recoupments
          </h1>
          <p className="text-sm text-slate-500" data-id="element-192">
            Manage manual deductions and clawbacks applied to loan officers.
          </p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3" data-id="element-193">
        <Card className="shadow-sm border-slate-200/60 rounded-xl" data-id="element-194">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="element-195">
            <CardTitle className="text-sm font-medium text-slate-600" data-id="element-196">
              Total Fees
            </CardTitle>
            <Receipt className="h-4 w-4 text-slate-500" data-id="element-197" />
          </CardHeader>
          <CardContent data-id="element-198">
            <div className="text-2xl font-bold text-slate-900" data-id="element-199">
              {formatCurrency(totalFees)}
            </div>
            <p className="text-xs text-slate-500 mt-1" data-id="element-200">
              Compliance, marketing, etc.
            </p>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-slate-200/60 rounded-xl" data-id="element-201">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="element-202">
            <CardTitle className="text-sm font-medium text-slate-600" data-id="element-203">
              Total Recoupments
            </CardTitle>
            <AlertCircle className="h-4 w-4 text-red-500" data-id="element-204" />
          </CardHeader>
          <CardContent data-id="element-205">
            <div className="text-2xl font-bold text-slate-900" data-id="element-206">
              {formatCurrency(totalRecoupments)}
            </div>
            <p className="text-xs text-slate-500 mt-1" data-id="element-207">
              Clawbacks and early payoffs
            </p>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-slate-200/60 rounded-xl bg-slate-900 text-white" data-id="element-208">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="element-209">
            <CardTitle className="text-sm font-medium text-slate-300" data-id="element-210">
              Total Adjustments
            </CardTitle>
            <Receipt className="h-4 w-4 text-slate-400" data-id="element-211" />
          </CardHeader>
          <CardContent data-id="element-212">
            <div className="text-2xl font-bold text-white" data-id="element-213">
              {formatCurrency(totalFees + totalRecoupments)}
            </div>
            <p className="text-xs text-slate-400 mt-1" data-id="element-214">
              Total amount owed by LOs
            </p>
          </CardContent>
        </Card>
      </div>

      {isAdding && <Card className="shadow-sm border-blue-200 bg-blue-50/30 rounded-xl" data-id="element-215">
          <CardHeader className="border-b border-blue-100 pb-4" data-id="element-216">
            <CardTitle className="text-lg text-blue-900" data-id="element-217">
              Add New Adjustment
            </CardTitle>
            <CardDescription className="text-blue-700/70" data-id="element-218">
              Manually apply a fee or recoupment to a user.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6" data-id="element-219">
            <form onSubmit={handleAdd} className="space-y-4" data-id="element-220">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-221">
                <div className="space-y-2" data-id="element-222">
                  <label className="text-sm font-medium text-slate-700" data-id="element-223">
                    User *
                  </label>
                  <select required value={userId} onChange={e => setUserId(e.target.value)} className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-224">
                    <option value="" data-id="element-225">Select a user...</option>
                    {MOCK_USERS.map(u => <option key={u.id} value={u.id} data-id="element-226">
                        {u.name} ({u.model})
                      </option>)}
                  </select>
                </div>

                <div className="space-y-2" data-id="element-227">
                  <label className="text-sm font-medium text-slate-700" data-id="element-228">
                    Related Loan (Optional)
                  </label>
                  <select value={loanId} onChange={e => setLoanId(e.target.value)} disabled={!userId || userLoans.length === 0} className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100 disabled:text-slate-400" data-id="element-229">
                    <option value="" data-id="element-230">None</option>
                    {userLoans.map(l => <option key={l.id} value={l.id} data-id="element-231">
                        {l.borrowerName} - {formatCurrency(l.loanAmount)} (
                        {l.fundedDate})
                      </option>)}
                  </select>
                </div>

                <div className="space-y-2" data-id="element-232">
                  <label className="text-sm font-medium text-slate-700" data-id="element-233">
                    Type *
                  </label>
                  <select required value={type} onChange={e => setType(e.target.value as AdjustmentType)} className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-234">
                    <option value="fee" data-id="element-235">Fee</option>
                    <option value="recoupment" data-id="element-236">Recoupment</option>
                  </select>
                </div>

                <div className="space-y-2" data-id="element-237">
                  <label className="text-sm font-medium text-slate-700" data-id="element-238">
                    Amount *
                  </label>
                  <div className="relative" data-id="element-239">
                    <span className="absolute left-3 top-2.5 text-slate-500" data-id="element-240">
                      $
                    </span>
                    <Input required type="number" min="0.01" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} className="pl-7" placeholder="0.00" data-id="element-241" />
                  </div>
                </div>

                <div className="space-y-2" data-id="element-242">
                  <label className="text-sm font-medium text-slate-700" data-id="element-243">
                    Label *
                  </label>
                  <Input required value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Compliance Fee" data-id="element-244" />
                </div>

                <div className="space-y-2" data-id="element-245">
                  <label className="text-sm font-medium text-slate-700" data-id="element-246">
                    Date *
                  </label>
                  <Input required type="date" value={date} onChange={e => setDate(e.target.value)} data-id="element-247" />
                </div>

                <div className="space-y-2 md:col-span-2" data-id="element-248">
                  <label className="text-sm font-medium text-slate-700" data-id="element-249">
                    Note (Optional)
                  </label>
                  <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Additional details..." data-id="element-250" />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-blue-100" data-id="element-251">
                <Button type="button" variant="outline" onClick={() => setIsAdding(false)} data-id="element-252">
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-id="element-253">
                  Save Adjustment
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>}

      <Card className="flex flex-col h-[calc(100vh-22rem)] shadow-sm border-slate-200/60 bg-white rounded-xl" data-id="element-254">
        <CardHeader className="border-b border-slate-100 shrink-0 flex flex-row items-center justify-between pb-4" data-id="element-255">
          <div data-id="element-256">
            <CardTitle data-id="element-257">Adjustment Ledger</CardTitle>
            <CardDescription data-id="element-258">
              All manual fees and recoupments applied.
            </CardDescription>
          </div>
          <div className="flex items-center gap-4" data-id="element-259">
            <div className="relative w-64" data-id="element-260">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" data-id="element-261" />
              <Input placeholder="Search adjustments..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 bg-slate-50 border-slate-200 focus:bg-white" data-id="element-262" />
            </div>
            <Button onClick={() => setIsAdding(true)} disabled={isAdding} className="bg-slate-900 text-white hover:bg-slate-800" data-id="element-263">
              <Plus className="mr-2 h-4 w-4" data-id="element-264" /> Add Adjustment
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto p-0" data-id="element-265">
          <Table data-id="element-266">
            <TableHeader className="sticky top-0 bg-slate-50/90 backdrop-blur-sm shadow-sm z-10 border-b border-slate-200" data-id="element-267">
              <TableRow className="hover:bg-transparent" data-id="element-268">
                <TableHead className="pl-6 font-semibold text-slate-600" data-id="element-269">
                  Date
                </TableHead>
                <TableHead className="font-semibold text-slate-600" data-id="element-270">
                  User
                </TableHead>
                <TableHead className="font-semibold text-slate-600" data-id="element-271">
                  Type & Label
                </TableHead>
                <TableHead className="font-semibold text-slate-600" data-id="element-272">
                  Related Loan
                </TableHead>
                <TableHead className="text-right font-semibold text-slate-600" data-id="element-273">
                  Amount
                </TableHead>
                <TableHead className="text-right pr-6 font-semibold text-slate-600" data-id="element-274">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-id="element-275">
              {filteredAdjustments.map(adj => <TableRow key={adj.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-276">
                  <TableCell className="pl-6 text-slate-500 whitespace-nowrap" data-id="element-277">
                    {new Date(adj.date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
                  </TableCell>
                  <TableCell data-id="element-278">
                    {adj.user ? <div className="flex items-center gap-3" data-id="element-279">
                        <img src={adj.user.avatar} alt={adj.user.name} className="h-8 w-8 rounded-full bg-slate-200" data-id="element-280" />
                        <span className="font-medium text-slate-900" data-id="element-281">
                          {adj.user.name}
                        </span>
                      </div> : <span className="text-slate-400" data-id="element-282">Unknown User</span>}
                  </TableCell>
                  <TableCell data-id="element-283">
                    <div className="flex flex-col gap-1" data-id="element-284">
                      <div className="flex items-center gap-2" data-id="element-285">
                        <Badge variant="outline" className={cn('text-[10px] uppercase tracking-wider font-bold', adj.type === 'fee' ? 'text-slate-600 border-slate-200 bg-slate-100' : 'text-red-600 border-red-200 bg-red-50')} data-id="element-286">
                          {adj.type}
                        </Badge>
                        <span className="font-medium text-slate-900" data-id="element-287">
                          {adj.label}
                        </span>
                      </div>
                      {adj.note && <span className="text-xs text-slate-500 line-clamp-1" data-id="element-288">
                          {adj.note}
                        </span>}
                    </div>
                  </TableCell>
                  <TableCell data-id="element-289">
                    {adj.loanLabel ? <span className="text-sm text-slate-600" data-id="element-290">
                        {adj.loanLabel}
                      </span> : <span className="text-sm text-slate-400" data-id="element-291">—</span>}
                  </TableCell>
                  <TableCell className="text-right font-bold text-red-600" data-id="element-292">
                    -{formatCurrency(adj.amount)}
                  </TableCell>
                  <TableCell className="text-right pr-6" data-id="element-293">
                    <Button variant="ghost" size="icon" onClick={() => removeAdjustment(adj.id)} className="text-slate-400 hover:text-red-600 hover:bg-red-50" data-id="element-294">
                      <Trash2 className="h-4 w-4" data-id="element-295" />
                    </Button>
                  </TableCell>
                </TableRow>)}
              {filteredAdjustments.length === 0 && <TableRow data-id="element-296">
                  <TableCell colSpan={6} className="h-32 text-center text-slate-500" data-id="element-297">
                    No adjustments found.
                  </TableCell>
                </TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>;
}